/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
    AddComment,
    DeleteComment,
    DownloadAttachment,
    LazyLoadComments,
    LoadCodById,
    LoadComments,
    LoadCreditReview,
    LoadFinanceReview,
    LoadInvestigationReviewDomestic,
    LoadInvestigationReviewInternational,
    LoadManagerReview,
    ReassignCod,
    ResetCodReview,
    SubmitCreditReview,
    SubmitFinanceReview,
    SubmitInvestigationReviewDomestic,
    SubmitInvestigationReviewInternational,
    SubmitManagerReview
} from './cod-review.action';
import {
    catchError,
    delay,
    EMPTY,
    filter,
    forkJoin,
    from,
    map,
    mergeMap,
    Observable,
    of,
    switchMap,
    tap,
    throwError,
    toArray,
    zip
} from 'rxjs';
import { CodService } from '../../shared/services/cod.service';
import { UpdateFormValue } from '@ngxs/form-plugin';
import { CodCreateForm } from '../models/cod-create.model';
import { StepConfig } from '../../shared/types/step-config.type';
import { RegionStepConfig } from '../components/steps/region/region.config';
import { CodMappingOperations } from '../operations/cod-mapping.operations';
import { CodStepOperations } from '../operations/cod-steps.operations';
import { GeneralCod } from '../models/cod.model';
import { AttachmentService } from '../services/attachment.service';
import {
    CreditReviewForm,
    FinanceReviewForm,
    InvestigationReviewDomesticForm,
    InvestigationReviewInternationalForm,
    ManagerReviewForm
} from '../models/cod-review.model';
import { SetOrderLineItems } from './cod.action';
import { IComment } from '@cod-monorepo/interfaces';
import { Page, PagedMetadata } from '../../shared/models/page-metadata.model';
import { CommentService } from '../../shared/services/comment.service';
import { UserManagementService } from '../../user-management/services/user-management.service';

// Delay to wait for the state-machine
const RELOAD_DELAY = 8000;
const PAGE_SIZE = 20;

interface CodReviewStateModel {
    form: {
        model?: CodCreateForm;
        dirty: boolean;
        status: string;
        errors: { [key: string]: string };
    };
    selectedCod: GeneralCod | null;
    steps: StepConfig[];
    loading: boolean;
    reassigning: boolean;
    attachmentKeys: string[];

    submittingManagerReview: boolean;
    submittingManagerReviewError: string | null;
    submittingManagerReviewSuccess: boolean;
    managerReviewForm: {
        model?: ManagerReviewForm;
        dirty: boolean;
        status: string;
        errors: { [key: string]: string };
    };

    submittingFinanceReview: boolean;
    submittingFinanceReviewError: string | null;
    submittingFinanceReviewSuccess: boolean;
    financeReviewForm: {
        model?: FinanceReviewForm;
        dirty: boolean;
        status: string;
        errors: { [key: string]: string };
    };

    submittingCreditReview: boolean;
    submittingCreditReviewError: string | null;
    submittingCreditReviewSuccess: boolean;
    creditReviewForm: {
        model?: CreditReviewForm;
        dirty: boolean;
        status: string;
        errors: { [key: string]: string };
    };

    submittingInvestigationReviewInt: boolean;
    submittingInvestigationReviewIntError: string | null;
    submittingInvestigationReviewIntSuccess: boolean;
    investigationReviewIntForm: {
        model?: InvestigationReviewInternationalForm;
        dirty: boolean;
        status: string;
        errors: { [key: string]: string };
    };

    submittingInvestigationReviewDomestic: boolean;
    submittingInvestigationReviewDomesticError: string | null;
    submittingInvestigationReviewDomesticSuccess: boolean;
    investigationReviewDomesticForm: {
        model?: InvestigationReviewDomesticForm;
        dirty: boolean;
        status: string;
        errors: { [key: string]: string };
    };

    comments: IComment[];
    commentsMetadata: PagedMetadata | null;
    isLoadingComments: boolean;
    isLazyLoadingComments: boolean;
    accessDenied: boolean;
}

@State<CodReviewStateModel>({
    name: 'codDetail',
    defaults: {
        form: {
            model: undefined,
            dirty: false,
            status: '',
            errors: {}
        },
        selectedCod: null,
        steps: [RegionStepConfig],
        loading: false,
        reassigning: false,
        attachmentKeys: [],

        submittingManagerReview: false,
        submittingManagerReviewError: null,
        submittingManagerReviewSuccess: false,
        managerReviewForm: {
            model: undefined,
            dirty: false,
            status: '',
            errors: {}
        },

        submittingFinanceReview: false,
        submittingFinanceReviewError: null,
        submittingFinanceReviewSuccess: false,
        financeReviewForm: {
            model: undefined,
            dirty: false,
            status: '',
            errors: {}
        },

        submittingCreditReview: false,
        submittingCreditReviewError: null,
        submittingCreditReviewSuccess: false,
        creditReviewForm: {
            model: undefined,
            dirty: false,
            status: '',
            errors: {}
        },

        submittingInvestigationReviewInt: false,
        submittingInvestigationReviewIntError: null,
        submittingInvestigationReviewIntSuccess: false,
        investigationReviewIntForm: {
            model: undefined,
            dirty: false,
            status: '',
            errors: {}
        },

        submittingInvestigationReviewDomestic: false,
        submittingInvestigationReviewDomesticError: null,
        submittingInvestigationReviewDomesticSuccess: false,
        investigationReviewDomesticForm: {
            model: undefined,
            dirty: false,
            status: '',
            errors: {}
        },

        comments: [],
        commentsMetadata: null,
        isLazyLoadingComments: false,
        isLoadingComments: false,
        accessDenied: false
    }
})
@Injectable()
export class CodReviewState {
    constructor(
        private codService: CodService,
        private commentService: CommentService,
        private attachmentService: AttachmentService,
        private userService: UserManagementService
    ) {}

    @Selector()
    public static selectedCod(state: CodReviewStateModel): GeneralCod | null {
        return state.selectedCod;
    }

    @Selector()
    public static loading(state: CodReviewStateModel): boolean {
        return state.loading;
    }

    @Selector()
    public static accessDenied(state: CodReviewStateModel): boolean {
        return state.accessDenied;
    }

    @Selector()
    static steps(state: CodReviewStateModel): StepConfig[] {
        return state.steps;
    }

    @Selector()
    public static submittingManagerReview(state: CodReviewStateModel): boolean {
        return state.submittingManagerReview;
    }

    @Selector()
    public static submittingManagerReviewError(state: CodReviewStateModel): string | null {
        return state.submittingManagerReviewError;
    }

    @Selector()
    public static submittingManagerReviewSuccess(state: CodReviewStateModel): boolean {
        return state.submittingManagerReviewSuccess;
    }

    @Selector()
    public static submittingCreditReview(state: CodReviewStateModel): boolean {
        return state.submittingCreditReview;
    }

    @Selector()
    public static submittingCreditReviewError(state: CodReviewStateModel): string | null {
        return state.submittingCreditReviewError;
    }

    @Selector()
    public static submittingCreditReviewSuccess(state: CodReviewStateModel): boolean {
        return state.submittingCreditReviewSuccess;
    }

    @Selector()
    public static submittingFinanceReview(state: CodReviewStateModel): boolean {
        return state.submittingFinanceReview;
    }

    @Selector()
    public static submittingFinanceReviewError(state: CodReviewStateModel): string | null {
        return state.submittingFinanceReviewError;
    }

    @Selector()
    public static submittingFinanceReviewSuccess(state: CodReviewStateModel): boolean {
        return state.submittingFinanceReviewSuccess;
    }

    @Selector()
    public static submittingInvestigationReviewInt(state: CodReviewStateModel): boolean {
        return state.submittingInvestigationReviewInt;
    }

    @Selector()
    public static submittingInvestigationReviewIntError(state: CodReviewStateModel): string | null {
        return state.submittingInvestigationReviewIntError;
    }

    @Selector()
    public static submittingInvestigationReviewIntSuccess(state: CodReviewStateModel): boolean {
        return state.submittingInvestigationReviewIntSuccess;
    }

    @Selector()
    public static submittingInvestigationReviewDomestic(state: CodReviewStateModel): boolean {
        return state.submittingInvestigationReviewDomestic;
    }

    @Selector()
    public static submittingInvestigationReviewErrorDomestic(state: CodReviewStateModel): string | null {
        return state.submittingInvestigationReviewDomesticError;
    }

    @Selector()
    public static submittingInvestigationReviewSuccessDomestic(state: CodReviewStateModel): boolean {
        return state.submittingInvestigationReviewDomesticSuccess;
    }

    @Selector()
    public static comments(state: CodReviewStateModel): IComment[] {
        return state.comments;
    }

    @Selector()
    public static commentsMetadata(state: CodReviewStateModel): PagedMetadata | null {
        return state.commentsMetadata;
    }

    @Selector()
    public static isLoadingComments(state: CodReviewStateModel): boolean {
        return state.isLoadingComments;
    }

    @Selector()
    public static isLazyLoadingComments(state: CodReviewStateModel): boolean {
        return state.isLazyLoadingComments;
    }

    @Selector()
    public static allCommentsLoaded(state: CodReviewStateModel): boolean {
        return !(state?.commentsMetadata?.hasNextPage ?? true);
    }

    @Selector()
    public static creditReviewFormModel(state: CodReviewStateModel): CreditReviewForm | undefined {
        return state.creditReviewForm.model;
    }

    @Selector()
    public static managerReviewFormModel(state: CodReviewStateModel): ManagerReviewForm | undefined {
        return state.managerReviewForm.model;
    }

    @Selector()
    public static financeReviewFormModel(state: CodReviewStateModel): FinanceReviewForm | undefined {
        return state.financeReviewForm.model;
    }

    @Selector()
    public static investigationReviewIntFormModel(
        state: CodReviewStateModel
    ): InvestigationReviewInternationalForm | undefined {
        return state.investigationReviewIntForm.model;
    }

    @Selector()
    public static investigationReviewDomesticFormModel(
        state: CodReviewStateModel
    ): InvestigationReviewDomesticForm | undefined {
        return state.investigationReviewDomesticForm.model;
    }

    @Action(LoadCodById)
    public loadCodById(
        ctx: StateContext<CodReviewStateModel>,
        action: LoadCodById
    ): Observable<[GeneralCod, { Key: string }[]]> {
        ctx.patchState({
            loading: true,
            accessDenied: false
        });

        return forkJoin([
            this.codService.loadCodById(action.id),
            this.attachmentService.getAttachments(action.id)
        ]).pipe(
            tap(([cod, attachments]) => {
                const keys = attachments.map(attachment => attachment.Key);
                const form = CodMappingOperations.mapDtoToForm(cod, keys);
                const steps = CodStepOperations.evaluateSteps(form!, 'VALID', 'VALID', this.getVersion(cod.createdAt!));

                ctx.dispatch(new SetOrderLineItems(cod.affectedOrderLines ?? []));

                ctx.patchState({
                    selectedCod: cod,
                    attachmentKeys: keys,
                    loading: false,
                    steps
                });

                ctx.dispatch(
                    new UpdateFormValue({
                        value: form,
                        path: 'codDetail.form'
                    })
                );
            }),
            catchError(error => {
                ctx.patchState({
                    loading: false,
                    accessDenied: error.status === 403
                });
                return EMPTY;
            })
        );
    }

    @Action(ReassignCod)
    public reassignCod(ctx: StateContext<CodReviewStateModel>, action: ReassignCod): Observable<void> {
        ctx.patchState({
            loading: true,
            reassigning: true,
            selectedCod: null
        });

        return this.codService.reassignCod(action.id, action.gid).pipe(
            delay(RELOAD_DELAY),
            tap(() => {
                ctx.dispatch(new LoadCodById(action.id));
                ctx.patchState({
                    reassigning: false
                });
            })
        );
    }

    @Action(SubmitCreditReview)
    public submitCreditReview(ctx: StateContext<CodReviewStateModel>, action: SubmitCreditReview): Observable<void> {
        const { selectedCod } = ctx.getState();

        if (!selectedCod || !selectedCod.id) {
            throw new Error('No selected cod');
        }

        ctx.patchState({
            submittingCreditReview: true
        });

        return of(action.data.creditReviewRelatedDocuments || []).pipe(
            mergeMap(docs =>
                docs.length
                    ? from(docs).pipe(
                          mergeMap(doc =>
                              this.attachmentService.uploadAttachment(
                                  selectedCod.id!,
                                  'creditReviewRelatedDocuments',
                                  doc.file!
                              )
                          ),
                          toArray()
                      )
                    : of([])
            ),
            switchMap(() => this.codService.submitCreditReview(selectedCod.id!, action.data)),
            tap(() => {
                ctx.patchState({
                    submittingCreditReview: false,
                    submittingCreditReviewSuccess: true,
                    loading: true,
                    selectedCod: null
                });
            }),
            delay(RELOAD_DELAY),
            tap(() => {
                ctx.dispatch(new LoadCodById(selectedCod.id!));
            })
        );
    }

    @Action(SubmitManagerReview)
    public submittManagerReview(ctx: StateContext<CodReviewStateModel>, action: SubmitManagerReview): Observable<void> {
        const { selectedCod } = ctx.getState();

        if (!selectedCod || !selectedCod.id) {
            throw new Error('No selected cod');
        }

        ctx.patchState({
            submittingManagerReview: true
        });

        return this.codService.submitMangerReview(selectedCod.id, action.data).pipe(
            tap(() => {
                ctx.patchState({
                    submittingManagerReview: false,
                    submittingManagerReviewSuccess: true,
                    loading: true,
                    selectedCod: null
                });
            }),
            delay(RELOAD_DELAY),
            tap(() => {
                ctx.dispatch(new LoadCodById(selectedCod.id!));
            })
        );
    }

    @Action(SubmitFinanceReview)
    public submitFinanceReview(ctx: StateContext<CodReviewStateModel>, action: SubmitFinanceReview): Observable<void> {
        const { selectedCod } = ctx.getState();

        if (!selectedCod || !selectedCod.id) {
            throw new Error('No selected cod');
        }

        ctx.patchState({
            submittingFinanceReview: true
        });

        return this.codService.submitFinanceReview(selectedCod.id, action.data).pipe(
            tap(() => {
                ctx.patchState({
                    submittingFinanceReview: false,
                    submittingFinanceReviewSuccess: true,
                    loading: true,
                    selectedCod: null
                });
            }),
            delay(RELOAD_DELAY),
            tap(() => {
                ctx.dispatch(new LoadCodById(selectedCod.id!));
            })
        );
    }

    @Action(SubmitInvestigationReviewInternational)
    public submitInvestigationReview(
        ctx: StateContext<CodReviewStateModel>,
        action: SubmitInvestigationReviewInternational
    ): Observable<void> {
        const { selectedCod } = ctx.getState();

        if (!selectedCod || !selectedCod.id) {
            throw new Error('No selected cod');
        }

        ctx.patchState({
            submittingInvestigationReviewInt: true
        });

        return of(action.data.investigationReviewRelatedDocuments || []).pipe(
            mergeMap(docs =>
                docs.length
                    ? from(docs).pipe(
                          mergeMap(doc =>
                              this.attachmentService.uploadAttachment(
                                  selectedCod.id!,
                                  'investigationReviewRelatedDocuments',
                                  doc.file!
                              )
                          ),
                          toArray()
                      )
                    : of([])
            ),
            switchMap(() => this.codService.submitInvestigationReviewInternational(selectedCod.id!, action.data)),
            tap(() => {
                ctx.patchState({
                    submittingInvestigationReviewInt: false,
                    submittingInvestigationReviewIntSuccess: true,
                    loading: true,
                    selectedCod: null
                });
            }),
            delay(RELOAD_DELAY),
            tap(() => {
                ctx.dispatch(new LoadCodById(selectedCod.id!));
            }),
            catchError(error => {
                ctx.patchState({
                    submittingInvestigationReviewInt: false,
                    submittingInvestigationReviewIntSuccess: false
                });
                console.error('Error during submission:', error);
                return throwError(() => error);
            })
        );
    }

    @Action(SubmitInvestigationReviewDomestic)
    public submitInvestigationReviewDomestic(
        ctx: StateContext<CodReviewStateModel>,
        action: SubmitInvestigationReviewDomestic
    ): Observable<void> {
        const { selectedCod } = ctx.getState();

        if (!selectedCod || !selectedCod.id) {
            throw new Error('No selected cod');
        }

        ctx.patchState({
            submittingInvestigationReviewDomestic: true
        });

        return of(action.data.investigationReviewRelatedDocuments || []).pipe(
            mergeMap(docs =>
                docs.length
                    ? from(docs).pipe(
                          mergeMap(doc =>
                              this.attachmentService.uploadAttachment(
                                  selectedCod.id!,
                                  'investigationReviewRelatedDocuments',
                                  doc.file!
                              )
                          ),
                          toArray()
                      )
                    : of([])
            ),
            toArray(),
            switchMap(() => this.codService.submitInvestigationReviewDomestic(selectedCod.id!, action.data)),
            tap(() => {
                ctx.patchState({
                    submittingInvestigationReviewDomestic: false,
                    submittingInvestigationReviewDomesticSuccess: true,
                    loading: true,
                    selectedCod: null
                });
            }),
            delay(RELOAD_DELAY),
            tap(() => {
                ctx.dispatch(new LoadCodById(selectedCod.id!));
            }),
            catchError(error => {
                ctx.patchState({
                    submittingInvestigationReviewDomestic: false,
                    submittingInvestigationReviewDomesticSuccess: false
                });
                console.error('Error during submission:', error);
                return throwError(() => error);
            })
        );
    }

    @Action(DownloadAttachment)
    public downloadAttachment(
        ctx: StateContext<CodReviewStateModel>,
        action: DownloadAttachment
    ): Observable<{ signedURL: string }> {
        const id = ctx.getState().selectedCod!.id!;

        if (!id) {
            throw new Error('No selected cod');
        }

        return this.attachmentService.downloadAttachment(ctx.getState().selectedCod!.id!, action.key);
    }

    @Action(ResetCodReview)
    public resetCodDetail(ctx: StateContext<CodReviewStateModel>): void {
        ctx.setState({
            form: {
                model: undefined,
                dirty: false,
                status: '',
                errors: {}
            },
            selectedCod: null,
            steps: [RegionStepConfig],
            loading: false,
            reassigning: false,
            attachmentKeys: [],

            submittingManagerReview: false,
            submittingManagerReviewError: null,
            submittingManagerReviewSuccess: false,
            managerReviewForm: {
                model: undefined,
                dirty: false,
                status: '',
                errors: {}
            },

            submittingFinanceReview: false,
            submittingFinanceReviewError: null,
            submittingFinanceReviewSuccess: false,
            financeReviewForm: {
                model: undefined,
                dirty: false,
                status: '',
                errors: {}
            },

            submittingCreditReview: false,
            submittingCreditReviewError: null,
            submittingCreditReviewSuccess: false,
            creditReviewForm: {
                model: undefined,
                dirty: false,
                status: '',
                errors: {}
            },

            submittingInvestigationReviewInt: false,
            submittingInvestigationReviewIntError: null,
            submittingInvestigationReviewIntSuccess: false,
            investigationReviewIntForm: {
                model: undefined,
                dirty: false,
                status: '',
                errors: {}
            },

            submittingInvestigationReviewDomestic: false,
            submittingInvestigationReviewDomesticError: null,
            submittingInvestigationReviewDomesticSuccess: false,
            investigationReviewDomesticForm: {
                model: undefined,
                dirty: false,
                status: '',
                errors: {}
            },

            comments: [],
            commentsMetadata: null,
            isLoadingComments: false,
            isLazyLoadingComments: false,
            accessDenied: false
        });
    }

    @Action(LoadCreditReview)
    public loadCreditReview(
        ctx: StateContext<CodReviewStateModel>,
        action: LoadCreditReview
    ): Observable<CreditReviewForm> {
        const id = action.id;
        const { attachmentKeys } = ctx.getState();

        return this.codService.loadCreditReview(id, attachmentKeys).pipe(
            tap(creditReview => {
                ctx.dispatch(
                    new UpdateFormValue({
                        value: creditReview,
                        path: 'codDetail.creditReviewForm'
                    })
                );
            })
        );
    }

    @Action(LoadInvestigationReviewDomestic)
    public loadInvestigationReviewDomestic(
        ctx: StateContext<CodReviewStateModel>,
        action: LoadInvestigationReviewDomestic
    ): Observable<InvestigationReviewDomesticForm> {
        const id = action.id;
        const { attachmentKeys } = ctx.getState();

        return this.codService.loadInvestigationReviewDomestic(id, attachmentKeys).pipe(
            tap(investigationReviewDomestic => {
                ctx.dispatch(
                    new UpdateFormValue({
                        value: investigationReviewDomestic,
                        path: 'codDetail.investigationReviewDomesticForm'
                    })
                );
            })
        );
    }

    @Action(LoadInvestigationReviewInternational)
    public loadInvestigationReviewInternational(
        ctx: StateContext<CodReviewStateModel>,
        action: LoadInvestigationReviewInternational
    ): Observable<InvestigationReviewInternationalForm> {
        const { id } = action;
        const { attachmentKeys } = ctx.getState();

        return this.codService.loadInvestigationReviewInternational(id, attachmentKeys).pipe(
            tap(investigationReviewInternational => {
                ctx.dispatch(
                    new UpdateFormValue({
                        value: investigationReviewInternational,
                        path: 'codDetail.investigationReviewIntForm'
                    })
                );
            })
        );
    }

    @Action(LoadManagerReview)
    public loadManagerReview(
        ctx: StateContext<CodReviewStateModel>,
        action: LoadManagerReview
    ): Observable<ManagerReviewForm> {
        const id = action.id;

        return this.codService.loadManagerReview(id).pipe(
            tap(managerReview => {
                ctx.dispatch(
                    new UpdateFormValue({
                        value: managerReview,
                        path: 'codDetail.managerReviewForm'
                    })
                );
            })
        );
    }

    @Action(LoadFinanceReview)
    public loadFinanceReview(
        ctx: StateContext<CodReviewStateModel>,
        action: LoadFinanceReview
    ): Observable<FinanceReviewForm> {
        const id = action.id;

        return this.codService.loadFinanceReview(id).pipe(
            tap(financeReview => {
                ctx.dispatch(
                    new UpdateFormValue({
                        value: financeReview,
                        path: 'codDetail.financeReviewForm'
                    })
                );
            })
        );
    }

    @Action(LoadComments)
    loadCods(ctx: StateContext<CodReviewStateModel>): Observable<Page<IComment>> {
        const state = ctx.getState();
        const id = state.selectedCod?.id;

        if (!id) {
            return EMPTY;
        }

        ctx.patchState({ isLoadingComments: true });

        return this.commentService.getAllComments(id, 0, PAGE_SIZE).pipe(
            mergeMap(result => {
                if (!result.items.length) {
                    ctx.patchState({
                        comments: result.items.flat(),
                        commentsMetadata: result.metadata,
                        isLazyLoadingComments: false,
                        isLoadingComments: false
                    });
                }

                const users = result.items.map(comment => this.userService.getUser(comment.createdBy));
                return zip(of(result), forkJoin(users));
            }),
            map(([result, users]) => {
                const commentsWithUsers = result.items.map((comment, index) => ({
                    ...comment,
                    user: users[index]
                }));

                return {
                    items: commentsWithUsers,
                    metadata: result.metadata
                } as Page<IComment>;
            }),
            catchError(() => {
                ctx.patchState({ isLoadingComments: false, isLazyLoadingComments: false });
                return of(void 0);
            }),
            filter(response => !!response),
            tap(response =>
                ctx.patchState({
                    comments: response.items.flat(),
                    commentsMetadata: response.metadata,
                    isLazyLoadingComments: false,
                    isLoadingComments: false
                })
            )
        );
    }

    @Action(LazyLoadComments)
    lazyLoadCods(ctx: StateContext<CodReviewStateModel>): Observable<Page<IComment>> {
        const state = ctx.getState();
        const page = Math.round(ctx.getState().comments.length / PAGE_SIZE);
        const id = state.selectedCod?.id;
        ctx.patchState({ isLazyLoadingComments: true });

        if (!id) {
            return of({ items: [], metadata: {} } as unknown as Page<IComment>);
        }

        return this.commentService.getAllComments(id, page, PAGE_SIZE).pipe(
            mergeMap(result => {
                const users = result.items.map(comment => this.userService.getUser(comment.createdBy));
                return zip(of(result), forkJoin(users));
            }),
            map(([result, users]) => {
                const commentsWithUsers = result.items.map((comment, index) => ({
                    ...comment,
                    user: users[index]
                }));
                return {
                    items: commentsWithUsers,
                    metadata: result.metadata
                } as Page<IComment>;
            }),
            catchError(() => {
                ctx.patchState({ isLazyLoadingComments: false });
                return of({ items: [], metadata: {} } as unknown as Page<IComment>);
            }),
            tap(result =>
                ctx.patchState({
                    comments: state.comments.concat(result.items.flat()),
                    commentsMetadata: result.metadata,
                    isLazyLoadingComments: false,
                    isLoadingComments: false
                })
            )
        );
    }

    @Action(AddComment)
    addComment(ctx: StateContext<CodReviewStateModel>, action: AddComment): Observable<IComment> {
        const state = ctx.getState();
        const id = state.selectedCod?.id;

        if (!id) {
            return EMPTY;
        }

        const newFiles = (action.attachments || []).filter(a => !!a.file);
        const existingKeys = (action.attachments || []).filter(a => !a.file).map(a => a.name);

        return (
            newFiles.length
                ? from(newFiles).pipe(
                      mergeMap(doc =>
                          this.attachmentService
                              .uploadAttachment(id, 'comments', doc.file!)
                              .pipe(map(() => `comments/${doc.name}`))
                      ),
                      toArray()
                  )
                : of([] as string[])
        ).pipe(
            switchMap(uploadedKeys => {
                const attachmentKeys = [...existingKeys, ...uploadedKeys];
                return this.commentService.createComment(id, {
                    content: action.comment,
                    ...(attachmentKeys.length ? { attachmentKeys } : {})
                });
            }),
            mergeMap(comment =>
                this.userService.getUser(comment.createdBy).pipe(
                    map(user => ({
                        ...comment,
                        user
                    }))
                )
            ),
            tap(commentWithUser => {
                ctx.patchState({
                    comments: [commentWithUser, ...state.comments]
                });
            })
        );
    }

    @Action(DeleteComment)
    deleteComment(ctx: StateContext<CodReviewStateModel>, action: DeleteComment): Observable<void> {
        const state = ctx.getState();
        const id = action.id;
        const codId = state.selectedCod?.id;

        if (!codId) {
            return EMPTY;
        }

        return this.commentService.deleteComment(codId, id).pipe(
            tap(() => {
                ctx.patchState({
                    comments: state.comments.filter(comment => comment.id !== id)
                });
            })
        );
    }

    private getVersion(createdAt: Date): string {
        const createdDate = new Date(createdAt);
        return createdDate >= new Date('2025-09-10') ? 'fy26' : 'fy25';
    }
}
